/**
 * KanbanTodayBadge — dopamine-loop counter shown above the Lead button on
 * scraper pages (Instagram / LinkedIn / YouTube).
 *
 * Behavior:
 *  - Fetches `/api/kanban/count` on mount
 *  - Listens for the global `agentelead:lead-converted` event so any
 *    `promote()` handler can dispatch a "+1" animation without prop drilling
 *  - Polls every 30s as a safety net (cheap count query)
 *
 * Visual: amber pill with a small flame icon, with a brief scale + green
 * pulse animation on increment.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Flame, ArrowUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30 * 1000;

export default function KanbanTodayBadge({ isPt }) {
  const [count, setCount] = useState(null);
  const [bumpAt, setBumpAt] = useState(0);   // timestamp of last increment
  const lastValueRef = useRef(null);

  const fetchCount = async () => {
    try {
      const { data } = await axios.get(`${API}/kanban/count`);
      const today = data.today ?? 0;
      // Animate only when the new value is HIGHER than the previous one.
      if (lastValueRef.current !== null && today > lastValueRef.current) {
        setBumpAt(Date.now());
      }
      lastValueRef.current = today;
      setCount(today);
    } catch (_e) {
      setCount(null);
    }
  };

  useEffect(() => {
    fetchCount();
    // Listen for the global event so a successful convert-to-lead refreshes
    // the badge instantly (no need to wait the 30s poll).
    const handler = () => fetchCount();
    window.addEventListener("agentelead:lead-converted", handler);
    const id = window.setInterval(fetchCount, POLL_MS);
    return () => {
      window.removeEventListener("agentelead:lead-converted", handler);
      window.clearInterval(id);
    };
  }, []);

  if (count === null) return null;

  const recentlyBumped = Date.now() - bumpAt < 1500;

  return (
    <div
      data-testid="kanban-today-badge"
      className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[10.5px] font-bold uppercase tracking-wide border transition-all duration-300
        ${recentlyBumped
          ? "bg-emerald-100 text-emerald-800 border-emerald-300 scale-110 shadow-md"
          : "bg-amber-50 text-amber-800 border-amber-200"}`}
    >
      <Flame className={`w-3 h-3 ${recentlyBumped ? "text-emerald-600 animate-pulse" : "text-amber-500"}`} />
      <span data-testid="kanban-today-count">{count}</span>
      <span className="font-semibold normal-case lowercase tracking-normal">
        {isPt ? "leads hoje" : "leads today"}
      </span>
      {recentlyBumped && (
        <ArrowUp
          data-testid="kanban-today-bump"
          className="w-3 h-3 text-emerald-700"
          style={{ animation: "alp-bump-pop 1s ease-out" }}
        />
      )}
      <style>{`
        @keyframes alp-bump-pop {
          0%   { transform: translateY(6px) scale(0.7); opacity: 0; }
          40%  { transform: translateY(-3px) scale(1.2); opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Convenience emitter for the convert-to-lead handlers. Call this right
// after a successful `axios.post(...convert-to-lead)` to trigger every
// mounted KanbanTodayBadge to refetch + animate.
export function notifyLeadConverted() {
  window.dispatchEvent(new CustomEvent("agentelead:lead-converted"));
}
